// SCR-STAFF-881 — نگاشت نقش‌های حساب (US-ACC-003، 004، 012): فهرست نقش‌ها با وضعیت نگاشت و شمار ردیف‌های صف خطا، پذیرش نگاشت پیشنهادی،
// نگاشت نقش (پیش‌فرض یا به ازای پارامتر، سطح مستأجر یا شعبه، تاریخ اثر) با بازپردازش خودکار صف خطا (AC-ACC-004.2)، لغو نگاشت، حالت تجمیع.
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type AccAccountView, type AccPostingGroup, type AccRoleView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDatePickerRHF,
  ListPage,
  StatusChip,
  toBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { accKeys, LIST_SEPARATOR, num, SCR_STAFF_881, useAccBranches } from './common';

const GROUPS: readonly AccPostingGroup[] = [
  'Sales',
  'Receipts',
  'Refunds',
  'Deferred',
  'Cash',
  'Insurance',
  'Compensation',
  'Inventory',
  'Expenses',
];

/** کلید ترجمه نقش (نقطه ← زیرخط). */
export const roleKey = (role: string) => `acc:role.${role.replaceAll('.', '_')}`;

interface MappingForm {
  parameter: string;
  branchId: string;
  effectiveFrom: string | null;
}

export function MappingsPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const canManage = access.accessMode === 'Full' && access.can('acc.mapping.manage');
  const roles = useQuery({
    queryKey: accKeys.roles(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/roles')),
  });
  const accounts = useQuery({
    queryKey: accKeys.accounts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/chart/accounts')),
    enabled: canManage,
  });
  const [editing, setEditing] = useState<AccRoleView | null>(null);
  const [account, setAccount] = useState<AccAccountView | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const { control, watch, reset, setValue } = useForm<MappingForm>({
    defaultValues: { parameter: '', branchId: '', effectiveFrom: toBusinessDate(new Date()) },
  });
  const form = watch();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: accKeys.all(session?.tenantId) });
  const accept = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/accounting/role-mappings:accept-suggested')),
    onSuccess: (data) => {
      const parts = [
        t('acc:mapping.accepted', {
          created: faDigits(num(data.created)),
          reprocessed: faDigits(num(data.reprocessed)),
        }),
      ];
      if (data.missingAccounts.length > 0) {
        parts.push(t('acc:mapping.missing', { roles: data.missingAccounts.join(LIST_SEPARATOR) }));
      }
      setMessage(parts.join(' '));
      refresh();
    },
  });
  const map = useMutation({
    mutationFn: () => {
      if (!editing || !account) {
        throw new Error('account');
      }
      return unwrap(
        tenantApi.POST('/api/v1/accounting/role-mappings', {
          body: {
            role: editing.key,
            parameter: form.parameter || null,
            branchId: form.branchId || null,
            accountId: account.id,
            effectiveFrom: form.effectiveFrom ?? null,
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      );
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (data) => {
      setEditing(null);
      setMessage(t('acc:mapping.reprocessed', { count: faDigits(num(data.reprocessed)) }));
      refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.DELETE('/api/v1/accounting/role-mappings/{id}', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const openMap = (role: AccRoleView) => {
    reset({ parameter: '', branchId: '', effectiveFrom: toBusinessDate(new Date()) });
    setAccount(null);
    setEditing(role);
  };
  const branches = useAccBranches();
  const columns: DataTableColumn<AccRoleView>[] = [
    {
      id: 'role',
      headerKey: 'acc:mapping.role',
      cell: (row) => (
        <span data-testid="acc-role" data-role={row.key}>
          {t(roleKey(row.key), { defaultValue: row.key })}
        </span>
      ),
    },
    { id: 'parameter', headerKey: 'acc:mapping.parameter', cell: (row) => t(`acc:param.${row.parameter}`) },
    {
      id: 'mapped',
      headerKey: 'acc:mapping.mapped_account',
      cell: (row) =>
        row.mappings.length === 0 ? (
          '—'
        ) : (
          <Stack spacing={0.5}>
            {row.mappings.map((mapping) => (
              <Stack
                key={mapping.id}
                direction="row"
                spacing={1}
                alignItems="center"
                data-testid="acc-mapping"
              >
                <bdi dir="ltr">{mapping.accountCode}</bdi>
                <span>{mapping.accountTitle}</span>
                {(mapping.parameterLabel ?? mapping.parameter) ? (
                  <Typography variant="caption">({mapping.parameterLabel ?? mapping.parameter})</Typography>
                ) : null}
                {canManage ? (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => revoke.mutate(mapping.id)}
                    data-testid="acc-mapping-revoke"
                  >
                    {t('acc:mapping.revoke')}
                  </Button>
                ) : null}
              </Stack>
            ))}
          </Stack>
        ),
    },
    {
      id: 'status',
      headerKey: 'acc:common.status',
      cell: (row) => (
        <span data-testid="acc-role-status" data-status={row.status}>
          <StatusChip
            tone={row.status === 'Mapped' ? 'done' : row.status === 'Partial' ? 'pending' : 'error'}
            labelKey={`acc:role_status.${row.status}`}
          />
        </span>
      ),
    },
    {
      id: 'failed',
      headerKey: 'acc:mapping.failed_entries',
      cell: (row) =>
        num(row.failedEntries) > 0 ? (
          <strong data-testid="acc-role-failed">{faDigits(num(row.failedEntries))}</strong>
        ) : (
          '—'
        ),
    },
    {
      id: 'actions',
      headerKey: 'acc:common.actions',
      cell: (row) =>
        canManage ? (
          <Button size="small" onClick={() => openMap(row)} data-testid="acc-role-map">
            {t('acc:mapping.map')}
          </Button>
        ) : null,
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_881}
      titleKey="acc:mapping.title"
      subtitleKey="acc:mapping.subtitle"
      actions={
        canManage ? (
          <Button
            variant="contained"
            onClick={() => accept.mutate()}
            disabled={accept.isPending}
            data-testid="acc-accept-suggested"
          >
            {t('acc:mapping.accept_suggested')}
          </Button>
        ) : undefined
      }
    >
      {message ? (
        <Alert
          severity="success"
          onClose={() => setMessage(null)}
          data-testid="acc-mapping-message"
          sx={{ mb: 2 }}
        >
          {message}
        </Alert>
      ) : null}
      {(accept.error ?? revoke.error) ? <ErrorState error={accept.error ?? revoke.error} compact /> : null}
      <DataTable
        captionKey="acc:mapping.title"
        columns={columns}
        rows={roles.data?.items}
        getRowId={(row) => row.key}
        isLoading={roles.isPending}
        error={roles.error}
        onRetry={() => void roles.refetch()}
        emptyTitleKey="acc:mapping.title"
      />
      {canManage ? <SettingsPanel /> : null}
      <FormDialog
        open={editing !== null}
        titleKey="acc:mapping.map"
        onSubmit={() => map.mutate()}
        onClose={() => setEditing(null)}
        isDirty={account !== null}
        isSubmitting={map.isPending}
        submitError={map.error}
      >
        {editing ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="subtitle2">
              {t('acc:mapping.map_title', { role: t(roleKey(editing.key), { defaultValue: editing.key }) })}
            </Typography>
            <Autocomplete
              options={(accounts.data?.items ?? []).filter((item) => item.isActive)}
              value={account}
              onChange={(_event, value) => setAccount(value)}
              getOptionLabel={(option) => `${option.code} — ${option.title}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('acc:mapping.account')}
                  slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': 'acc-mapping-account' } }}
                />
              )}
            />
            {editing.parameter !== 'None' ? (
              <TextField
                select
                label={t(`acc:param.${editing.parameter}`)}
                value={form.parameter}
                onChange={(event) => setValue('parameter', event.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'acc-mapping-parameter' } }}
              >
                <MenuItem value="">{t('acc:mapping.parameter_default')}</MenuItem>
                {editing.knownParameters.map((parameter) => (
                  <MenuItem key={parameter.value} value={parameter.value}>
                    {parameter.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <TextField
              select
              label={t('acc:mapping.branch_scope')}
              value={form.branchId}
              onChange={(event) => setValue('branchId', event.target.value)}
            >
              <MenuItem value="">{t('acc:common.tenant')}</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.branchId} value={branch.branchId}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>
            <JalaliDatePickerRHF
              control={control}
              name="effectiveFrom"
              labelKey="acc:mapping.effective_from"
            />
          </Stack>
        ) : null}
      </FormDialog>
    </ListPage>
  );
}

function SettingsPanel() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: accKeys.settings(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/settings')),
  });
  const [groups, setGroups] = useState<AccPostingGroup[] | null>(null);
  useEffect(() => {
    if (settings.data && groups === null) {
      setGroups([...settings.data.perTransactionGroups]);
    }
  }, [settings.data, groups]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.PUT('/api/v1/accounting/settings', { body: { perTransactionGroups: groups ?? [] } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: accKeys.settings(session?.tenantId) }),
  });
  if (settings.error) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} compact />;
  }
  const selected = groups ?? [];
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1">{t('acc:mapping.settings_title')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('acc:mapping.per_transaction_hint')}
      </Typography>
      <FormGroup row>
        {GROUPS.map((group) => (
          <FormControlLabel
            key={group}
            label={t(`acc:group.${group}`)}
            control={
              <Checkbox
                checked={selected.includes(group)}
                onChange={(event) =>
                  setGroups(
                    event.target.checked ? [...selected, group] : selected.filter((item) => item !== group),
                  )
                }
                slotProps={{
                  input: { 'aria-label': `${t('acc:mapping.per_transaction')} ${t(`acc:group.${group}`)}` },
                }}
              />
            }
          />
        ))}
      </FormGroup>
      {save.error ? <ErrorState error={save.error} compact /> : null}
      {save.isSuccess ? <Alert severity="success">{t('acc:mapping.saved')}</Alert> : null}
      <Button
        variant="outlined"
        onClick={() => save.mutate()}
        disabled={save.isPending || groups === null}
        data-testid="acc-settings-save"
      >
        {t('acc:common.save')}
      </Button>
    </Paper>
  );
}
